import { account } from './account.js';
import { device } from './device.js';
import { storage } from './storage.js';
import { t } from './i18n.js';
import { notify } from './notify.js';

/* Actualización de la app nativa. En Android (Cordova) descarga la APK publicada por el servidor
   y abre el instalador del sistema (plugins cordova-plugin-file y cordova-plugin-file-opener2).
   En Electron/web solo avisa con la URL de descarga. */

const APK_MIME = 'application/vnd.android.package-archive';

export const updater = {
    current: '',

    /** Versión disponible en el servidor para esta plataforma (APK en Android, bundle web en el resto). */
    available() {
        if (device.isCordova && device.isAndroid) return account.apkVersion || '';
        return account.serverVersion || '';
    },

    hasUpdate() {
        const v = this.available();
        return !!(v && this.current && v !== this.current);
    },

    canSelfInstall() {
        return !!(device.isCordova && device.isAndroid && window.cordova && window.cordova.file && window.cordova.plugins && window.cordova.plugins.fileOpener2);
    },

    downloadUrl() {
        return account.baseUrl() + '/apk';
    },

    /** Al arrancar: propone actualizar (una vez por versión) si hay algo nuevo. */
    checkOnStart() {
        if (!this.hasUpdate()) return;
        const v = this.available();
        if (storage.get('updateNotified') === v) return;
        storage.set('updateNotified', v);
        setTimeout(() => this.prompt(), 2500);
    },

    prompt() {
        const app = window.app;
        const v = this.available();
        const url = this.downloadUrl().replace(/^https?:\/\//, '');
        // La APK con servidor ya no se actualiza: la app que se mantiene es «Filoteca» (otro paquete, no se instala encima).
        if (device.isCordova && device.isAndroid) {
            app.dialog.confirm(t('update.migrate', { url }), t('update.title'), () => {
                if (window.IPTV && window.IPTV.openExternal) window.IPTV.openExternal(this.downloadUrl());
            });
            return;
        }
        if (this.canSelfInstall()) {
            app.dialog.confirm(t('update.text', { version: v }), t('update.title'), () => this.install());
        } else {
            app.dialog.alert(t('update.manual', { version: v, url }), t('update.title'));
        }
    },

    /* ---------------- Web / PWA: aviso para recargar ---------------- */

    /** En web la interfaz se sirve del servidor: basta recargar. Se comprueba al volver a la
        pestaña y cada 15 min, y se avisa una vez por versión con un botón para recargar. */
    startWebWatcher() {
        const check = () => this.checkWeb().catch(() => {});
        setTimeout(check, 30000);
        setInterval(check, 15 * 60000);
        document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
    },

    async checkWeb() {
        const cfg = await account.request('GET', '/api/config', { auth: false, timeout: 8000 });
        account.serverVersion = cfg.version || '';
        const v = account.serverVersion;
        if (!v || !this.current || v === this.current || this._webNotified === v) return false;
        this._webNotified = v;
        const app = window.app;
        if (this._webToast) { try { this._webToast.close(); } catch (e) { /* ya cerrado */ } }
        this._webToast = app.toast.create({
            text: '<span class="mi" style="vertical-align:-6px;margin-right:8px">system_update</span>' + t('update.webReady'),
            closeButton: true,
            closeButtonText: t('update.reload'),
            closeTimeout: 0,
            position: 'bottom',
            cssClass: 'app-toast app-toast-update',
            on: { closeButtonClick: () => this.reloadWeb() }
        });
        this._webToast.open();
        return true;
    },

    async reloadWeb() {
        try {
            if (navigator.serviceWorker) {
                const reg = await navigator.serviceWorker.getRegistration();
                if (reg) await reg.update();
            }
        } catch (e) { /* sin service worker */ }
        location.reload();
    },

    /** Desde Ajustes: comprueba de nuevo contra el servidor. */
    async checkNow() {
        try {
            const cfg = await account.request('GET', '/api/config', { auth: false, timeout: 8000 });
            account.serverVersion = cfg.version || '';
            account.apkVersion = cfg.apkVersion || '';
        } catch (e) {
            notify.error(t('common.errorNetwork'));
            return;
        }
        if (this.hasUpdate()) this.prompt();
        else notify.toast(t('update.upToDate'), { icon: 'check' });
    },

    async install() {
        const app = window.app;
        const progress = app.dialog.progress(t('update.downloading'), 0);
        try {
            const blob = await this._download((pct) => progress.setProgress(pct));
            progress.setText(t('update.installing'));
            const entry = await this._writeFile(blob);
            progress.close();
            await new Promise((resolve, reject) => {
                window.cordova.plugins.fileOpener2.open(entry.nativeURL || entry.toURL(), APK_MIME, { success: resolve, error: reject });
            });
        } catch (err) {
            progress.close();
            console.warn('[updater]', err);
            app.dialog.alert(t('update.failed', { url: this.downloadUrl().replace(/^https?:\/\//, '') }), t('update.title'));
        }
    },

    _download(onProgress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', account.baseUrl() + '/downloads/filoteca.apk?v=' + encodeURIComponent(this.available()), true);
            xhr.responseType = 'blob';
            xhr.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
            xhr.onload = () => (xhr.status === 200 ? resolve(xhr.response) : reject(new Error('HTTP ' + xhr.status)));
            xhr.onerror = () => reject(new Error('network'));
            xhr.send();
        });
    },

    _writeFile(blob) {
        const dir = window.cordova.file.externalCacheDirectory || window.cordova.file.cacheDirectory;
        return new Promise((resolve, reject) => {
            window.resolveLocalFileSystemURL(dir, (dirEntry) => {
                dirEntry.getFile('filoteca.apk', { create: true, exclusive: false }, (fileEntry) => {
                    fileEntry.createWriter((writer) => {
                        writer.onwriteend = () => resolve(fileEntry);
                        writer.onerror = reject;
                        writer.write(blob);
                    }, reject);
                }, reject);
            }, reject);
        });
    }
};
